import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  fetchAllExhibits,
  reorderExhibits,
  deleteExhibit,
  updateExhibit,
  AuthError,
  type Exhibit,
} from "@/lib/api";
import { PinModal } from "./PinModal";
import { ExhibitModal } from "./ExhibitModal";
import { ExhibitListItem } from "./ExhibitListItem";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Plus, Eye } from "lucide-react";
import { colors, fonts } from "@/lib/theme";

type VisibilityFilter = "all" | "visible" | "hidden";

/**
 * Admin page for managing exhibits
 * Features: drag-and-drop reordering, visibility toggle, create/edit/delete
 */
export function AdminGallery() {
  const navigate = useNavigate();
  const [pin, setPin] = useState<string | null>(null);
  const [exhibits, setExhibits] = useState<Exhibit[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingExhibit, setEditingExhibit] = useState<Exhibit | null>(null);
  const [filterVisibility, setFilterVisibility] = useState<VisibilityFilter>("all");

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Load saved PIN from localStorage (expires after 30 days)
  useEffect(() => {
    const savedPin = localStorage.getItem("admin_pin");
    const savedAt = localStorage.getItem("admin_pin_saved_at");

    if (savedPin && savedAt) {
      const thirtyDays = 30 * 24 * 60 * 60 * 1000;
      const isExpired = Date.now() - parseInt(savedAt, 10) > thirtyDays;

      if (isExpired) {
        localStorage.removeItem("admin_pin");
        localStorage.removeItem("admin_pin_saved_at");
      } else {
        setPin(savedPin);
      }
    }
  }, []);

  // Fetch exhibits when PIN is available
  useEffect(() => {
    if (pin) loadExhibits();
  }, [pin]);

  const clearPin = () => {
    setPin(null);
    localStorage.removeItem("admin_pin");
    localStorage.removeItem("admin_pin_saved_at");
  };

  const loadExhibits = () => {
    if (!pin) return;
    setLoading(true);
    fetchAllExhibits(pin)
      .then(setExhibits)
      .catch((error) => {
        if (error instanceof AuthError) {
          clearPin();
        } else {
          console.error(error);
        }
      })
      .finally(() => setLoading(false));
  };

  const handlePinSuccess = (enteredPin: string) => {
    setPin(enteredPin);
    localStorage.setItem("admin_pin", enteredPin);
    localStorage.setItem("admin_pin_saved_at", Date.now().toString());
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setExhibits((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      const newOrder = arrayMove(items, oldIndex, newIndex);

      if (pin) {
        reorderExhibits(newOrder.map((e) => e.id), pin).catch(console.error);
      }

      return newOrder;
    });
  };

  const handleDelete = async (id: number) => {
    if (!pin || !confirm("Are you sure you want to delete this exhibit?")) return;

    try {
      await deleteExhibit(id, pin);
      loadExhibits();
    } catch (error) {
      console.error("Failed to delete:", error);
      alert("Failed to delete exhibit");
    }
  };

  const handleToggleVisibility = async (exhibit: Exhibit) => {
    if (!pin) return;

    try {
      await updateExhibit(exhibit.id, { visible: !exhibit.visible }, pin);
      setExhibits((prev) =>
        prev.map((e) => (e.id === exhibit.id ? { ...e, visible: !e.visible } : e))
      );
    } catch (error) {
      console.error("Failed to toggle visibility:", error);
    }
  };

  const openCreateModal = () => {
    setEditingExhibit(null);
    setModalOpen(true);
  };

  const openEditModal = (exhibit: Exhibit) => {
    setEditingExhibit(exhibit);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingExhibit(null);
  };

  const handleModalSuccess = () => {
    closeModal();
    loadExhibits();
  };

  // Show PIN modal if not authenticated
  if (!pin) {
    return <PinModal onSuccess={handlePinSuccess} />;
  }

  const filteredExhibits =
    filterVisibility === "all"
      ? exhibits
      : exhibits.filter((e) => (filterVisibility === "visible" ? e.visible : !e.visible));

  return (
    <div style={{ backgroundColor: colors.background, minHeight: "100vh" }}>
      <AdminHeader onBack={() => navigate("/")} onCreate={openCreateModal} />

      <div className="max-w-4xl mx-auto px-4">
        <VisibilityFilters value={filterVisibility} onChange={setFilterVisibility} />
      </div>

      <main className="max-w-4xl mx-auto px-4 pb-16">
        {loading ? (
          <p style={{ color: colors.textMuted }}>Loading...</p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={filteredExhibits.map((e) => e.id)} strategy={verticalListSortingStrategy}>
              {filteredExhibits.map((exhibit) => (
                <ExhibitListItem
                  key={exhibit.id}
                  exhibit={exhibit}
                  onEdit={() => openEditModal(exhibit)}
                  onDelete={() => handleDelete(exhibit.id)}
                  onToggleVisibility={() => handleToggleVisibility(exhibit)}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}

        {filteredExhibits.length === 0 && !loading && (
          <p className="text-center" style={{ color: colors.textMuted }}>
            No exhibits found
          </p>
        )}
      </main>

      {modalOpen && (
        <ExhibitModal
          exhibit={editingExhibit}
          pin={pin}
          onClose={closeModal}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
}

/**
 * Admin page header with back button and create action
 */
interface AdminHeaderProps {
  onBack: () => void;
  onCreate: () => void;
}

function AdminHeader({ onBack, onCreate }: AdminHeaderProps) {
  return (
    <header className="py-6 md:py-8 px-4 max-w-4xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-2 mb-4"
        style={{ color: colors.primary }}
      >
        <ArrowLeft size={20} />
        Back to Gallery
      </button>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1
          className="text-2xl md:text-3xl"
          style={{ fontFamily: fonts.serif, color: colors.text }}
        >
          Manage Exhibits
        </h1>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => window.open("/header-preview", "_blank")}
            className="flex-1 md:flex-none"
            style={{ borderColor: colors.primary, color: colors.primary }}
          >
            <Eye size={20} className="mr-2" />
            <span className="hidden sm:inline">Preview </span>Headers
          </Button>
          <Button
            onClick={onCreate}
            className="flex-1 md:flex-none"
            style={{ backgroundColor: colors.primary, color: colors.surface }}
          >
            <Plus size={20} className="mr-2" />
            New Exhibit
          </Button>
        </div>
      </div>
    </header>
  );
}

/**
 * Filter buttons for visibility
 */
interface VisibilityFiltersProps {
  value: VisibilityFilter;
  onChange: (value: VisibilityFilter) => void;
}

function VisibilityFilters({ value, onChange }: VisibilityFiltersProps) {
  const filters: VisibilityFilter[] = ["all", "visible", "hidden"];

  return (
    <div className="flex gap-2 mb-4 md:mb-6">
      {filters.map((filter) => (
        <button
          key={filter}
          onClick={() => onChange(filter)}
          className="px-4 py-2.5 md:py-2 rounded transition-colors text-sm md:text-base"
          style={{
            backgroundColor: value === filter ? colors.primary : colors.surface,
            color: value === filter ? colors.surface : colors.text,
          }}
        >
          {filter.charAt(0).toUpperCase() + filter.slice(1)}
        </button>
      ))}
    </div>
  );
}
